import { MODULES, type Flag, type Level, type Module } from './modules.ts';

export interface RolePermissions {
  readonly modules: Readonly<Record<Module, Level>>;
  readonly flags: readonly Flag[];
}

export const BUILT_IN_ROLE_IDS = [
  'role-admin',
  'role-project-manager',
  'role-office',
  'role-field',
  'role-sub',
  'role-client',
] as const;

export type BuiltInRoleId = (typeof BUILT_IN_ROLE_IDS)[number];

/** Start from `none` for every module, then name only what is granted. */
function permissions(
  granted: Partial<Record<Module, Level>>,
  flags: readonly Flag[] = [],
): RolePermissions {
  const modules = Object.fromEntries(MODULES.map((m) => [m, 'none'])) as Record<Module, Level>;
  for (const [module, level] of Object.entries(granted)) {
    modules[module as Module] = level as Level;
  }
  return { modules, flags };
}

const everyModuleAdmin = Object.fromEntries(MODULES.map((m) => [m, 'admin'])) as Record<
  Module,
  Level
>;

/**
 * The six templates from docs/PERMISSIONS.md section 4.
 *
 * These are starting points an administrator clones and edits, not a fixed
 * set. Note what the external roles do *not* get: neither sub nor client
 * carries `seeBuilderCost`, so no amount of module access exposes margin.
 */
export const BUILT_IN_ROLES: Readonly<Record<BuiltInRoleId, { name: string } & RolePermissions>> = {
  'role-admin': {
    name: 'Administrator',
    modules: everyModuleAdmin,
    flags: [
      'seeBuilderCost',
      'releaseFinancials',
      'approveShifts',
      'approveEstimates',
      'manageRoles',
      'manageMembers',
      'viewAllJobs',
      'exportData',
      'markReadyForPayment',
      'recordPayments',
      'editSchedulePublished',
      'deleteOthersRecords',
    ],
  },

  'role-project-manager': {
    name: 'Project manager',
    ...permissions(
      {
        jobs: 'edit',
        schedule: 'admin',
        dailyLogs: 'edit',
        /**
         * `admin`, raised from `edit` on 2026-10-21 at the maintainer's
         * direction.
         *
         * The checklist template library (P3-09b) is gated on `todos:
         * admin`, and at `edit` a project manager could apply a standard
         * checklist but never fix a wrong row in one — narrower than
         * `scheduleTemplates`, which a PM may already edit. A project
         * manager is who actually knows what the standard checks are.
         *
         * It also lets a PM **delete a to-do** on a job they are on,
         * which is the only other thing `todos: admin` gates. That is
         * consistent rather than a surprise: deleting a *schedule item*
         * needs only `schedule: edit`, which this role already holds, so
         * a to-do is the lesser of the two.
         */
        todos: 'admin',
        changeOrders: 'admin',
        selections: 'admin',
        warranties: 'edit',
        specifications: 'edit',
        timeClock: 'admin',
        files: 'edit',
        messaging: 'edit',
        costCodes: 'view',
        // `docs/PERMISSIONS.md` §4 lists these as one row — "costCodes/
        // catalog | admin | view | edit | none | none | none" — and this
        // role had the first and not the second, so a project manager
        // could see the chart of accounts and not the catalog. Nothing
        // read the catalog until P5-03, which is why nothing failed.
        catalog: 'view',
        bids: 'edit',
        rfis: 'admin',
        estimates: 'edit',
        budget: 'edit',
        purchaseOrders: 'edit',
        bills: 'edit',
        invoices: 'view',
        payments: 'view',
        reports: 'view',
        directory: 'view',
        // A project manager books plant onto their own schedule, which is
        // what the conflict warning is for. They do not maintain the
        // register's commercial side — that is the office's.
        equipment: 'edit',
      },
      [
        'seeBuilderCost',
        'releaseFinancials',
        'approveShifts',
        'editSchedulePublished',
        'exportData',
      ],
    ),
  },

  'role-office': {
    name: 'Office and accounting',
    ...permissions(
      {
        jobs: 'view',
        schedule: 'view',
        dailyLogs: 'view',
        todos: 'edit',
        changeOrders: 'edit',
        selections: 'edit',
        warranties: 'view',
        specifications: 'view',
        timeClock: 'view',
        files: 'edit',
        messaging: 'edit',
        costCodes: 'edit',
        catalog: 'edit',
        bids: 'edit',
        rfis: 'edit',
        estimates: 'edit',
        budget: 'edit',
        purchaseOrders: 'edit',
        bills: 'admin',
        invoices: 'admin',
        payments: 'admin',
        reports: 'view',
        directory: 'edit',
        // The register's commercial side — what a machine cost, when it was
        // bought, which cost code its depreciation lands on — is accounting
        // work, so the office holds the same level as the chart of accounts.
        equipment: 'edit',
        integrations: 'view',
      },
      [
        'seeBuilderCost',
        'releaseFinancials',
        'markReadyForPayment',
        'recordPayments',
        'exportData',
      ],
    ),
  },

  'role-field': {
    name: 'Field',
    ...permissions({
      jobs: 'view',
      schedule: 'view',
      dailyLogs: 'edit',
      todos: 'edit',
      selections: 'view',
      warranties: 'view',
      specifications: 'view',
      timeClock: 'edit',
      /**
       * A crew member **raises** an RFI, which is the ordinary way one
       * starts: somebody on site cannot read a drawing and asks. They
       * hold `changeOrders` and `selections` at `none` or `view`, so this
       * is the module that lets them ask about something they cannot
       * change.
       */
      rfis: 'edit',
      files: 'edit',
      messaging: 'edit',
      // Read-only, deliberately. A crew needs to know which machine is
      // meant to be on site today; what it cost and when it was bought is
      // not their business, and the register is not theirs to edit.
      equipment: 'view',
    }),
  },

  'role-sub': {
    name: 'Subcontractor or vendor',
    ...permissions({
      jobs: 'view',
      schedule: 'view',
      dailyLogs: 'view',
      todos: 'edit',
      changeOrders: 'view',
      warranties: 'edit',
      specifications: 'view',
      files: 'view',
      messaging: 'edit',
      // A trade asks more questions than anybody. They see the ones they
      // raised and the ones put to them, and no others — the rules do
      // that per document, as for warranty claims.
      rfis: 'edit',
      bids: 'edit',
      purchaseOrders: 'view',
      bills: 'edit',
    }),
  },

  'role-client': {
    name: 'Client',
    ...permissions({
      jobs: 'view',
      schedule: 'view',
      dailyLogs: 'view',
      todos: 'edit',
      changeOrders: 'view',
      selections: 'view',
      warranties: 'view',
      specifications: 'view',
      files: 'view',
      messaging: 'edit',
      invoices: 'view',
      payments: 'view',
      // `docs/PERMISSIONS.md` §4 gives the client "view owner budget (if
      // enabled)". It grants no sight of `budgetLines`, whose rule also
      // requires an internal member — it is what lets an owner read
      // `ownerBudgetLines`, the derived mirror that carries owner pricing
      // and no cost. The "(if enabled)" half is the job's
      // `portal.modules.budget`, honoured by the portal.
      budget: 'view',
    }),
  },
};
